import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useTheme } from "styled-components";
import { Container, MainContent } from "./styles";
import EmptyState from "../../components/EmptyState";
import api from "../../services/api";
import apiSped from "../../services/apiSped";
import {
  GoPackage,
  GoZap,
  GoGraph,
  GoCalendar,
  GoSearch,
  GoStack,
  GoArrowUp,
  GoArrowDown,
  GoLaw,
  GoDatabase,
  GoCreditCard,
  GoSync,
  GoAlert,
} from "react-icons/go";
import { BiSpreadsheet } from "react-icons/bi";
import { HiOutlineChartBar, HiOutlineSearch } from "react-icons/hi";
import { FiLoader } from "react-icons/fi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import ProductDetail from "./product";
import ProductTaxKPIs from "./components/ProductTaxKPIs";
import EntityListRow, { EntityListSkeleton, getTagColor } from "../../components/EntityListRow";
import MonthlyChart from "./components/MonthlyChart";

// Mapa de setores NCM (baseado nos 2 primeiros dígitos do código NCM)
const NCM_SETOR_MAP = {
  "01": "Produtos Animais",
  "02": "Carnes e Miudezas",
  "03": "Peixes e Crustáceos",
  "04": "Laticínios",
  "05": "Outros Produtos Animais",
  "06": "Plantas Vivas",
  "07": "Hortaliças e Legumes",
  "08": "Frutas",
  "09": "Café e Chá",
  "10": "Cereais",
  "11": "Produtos de Moagem",
  "12": "Sementes e Oleaginosos",
  "13": "Gomas e Resinas",
  "14": "Matérias Vegetais",
  "15": "Gorduras e Óleos",
  "16": "Preparações de Carnes",
  "17": "Açúcares",
  "18": "Cacau",
  "19": "Preparações de Cereais",
  "20": "Preparações de Vegetais",
  "21": "Preparações Alimentícias",
  "22": "Bebidas",
  "23": "Resíduos Alimentares",
  "24": "Tabaco",
  "25": "Sal e Minerais",
  "26": "Minérios e Escórias",
  "27": "Combustíveis e Óleos",
  "28": "Produtos Químicos Inorgânicos",
  "29": "Produtos Químicos Orgânicos",
  "30": "Produtos Farmacêuticos",
  "31": "Adubos e Fertilizantes",
  "32": "Tintas e Corantes",
  "33": "Perfumaria e Cosméticos",
  "34": "Sabões e Detergentes",
  "35": "Colas e Enzimas",
  "36": "Pólvoras e Explosivos",
  "37": "Produtos Fotográficos",
  "38": "Produtos Químicos Diversos",
  "39": "Plásticos",
  "40": "Borracha",
  "41": "Peles e Couros",
  "42": "Artigos de Couro",
  "43": "Peleteria",
  "44": "Madeira",
  "45": "Cortiça",
  "46": "Cestaria",
  "47": "Pastas de Celulose",
  "48": "Papel e Cartão",
  "49": "Livros e Impressos",
  "50": "Seda",
  "51": "Lã",
  "52": "Algodão",
  "53": "Outras Fibras Têxteis",
  "54": "Filamentos Sintéticos",
  "55": "Fibras Sintéticas",
  "56": "Pastas e Feltros",
  "57": "Tapetes",
  "58": "Tecidos Especiais",
  "59": "Tecidos Impregnados",
  "60": "Tecidos de Malha",
  "61": "Vestuário de Malha",
  "62": "Vestuário Não de Malha",
  "63": "Artigos de Tecido",
  "64": "Calçados",
  "65": "Chapéus",
  "66": "Guarda-chuvas",
  "67": "Penas e Flores",
  "68": "Pedras e Cimento",
  "69": "Cerâmica",
  "70": "Vidro",
  "71": "Joias e Bijuterias",
  "72": "Ferro e Aço",
  "73": "Obras de Ferro ou Aço",
  "74": "Cobre",
  "75": "Níquel",
  "76": "Alumínio",
  "78": "Chumbo",
  "79": "Zinco",
  "80": "Estanho",
  "81": "Outros Metais Comuns",
  "82": "Ferramentas",
  "83": "Obras Diversas de Metal",
  "84": "Máquinas e Equipamentos",
  "85": "Equipamentos Elétricos",
  "86": "Veículos Ferroviários",
  "87": "Veículos Automóveis",
  "88": "Aeronaves",
  "89": "Embarcações",
  "90": "Instrumentos de Precisão",
  "91": "Relógios",
  "92": "Instrumentos Musicais",
  "93": "Armas e Munições",
  "94": "Móveis",
  "95": "Brinquedos",
  "96": "Obras Diversas",
  "97": "Objetos de Arte",
  "99": "Outros",
};

// Função para gerar cores de produtos
const getProductColor = (codigo) => {
  const colors = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
    "#ec4899",
    "#84cc16",
    "#f97316",
    "#6366f1",
  ];
  const hash =
    codigo?.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0;
  return colors[hash % colors.length];
};

// Função para formatar números
const formatNumber = (num) => {
  if (!num) return "R$ 0,00";
  return `R$ ${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Função para formatar números de forma compacta (K, M)
const formatCompact = (num) => {
  if (!num || num === 0) return "R$ 0";
  
  const absNum = Math.abs(num);
  
  if (absNum >= 1000000) {
    return `R$ ${(num / 1000000).toFixed(1)}M`;
  } else if (absNum >= 1000) {
    return `R$ ${(num / 1000).toFixed(1)}K`;
  }
  
  return `R$ ${num.toFixed(0)}`;
};

// Função para formatar moeda
const formatCurrency = (num) => {
  if (!num) return "R$ 0,00";
  return `R$ ${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default function Products() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Tab ativa persistida
  const [activeTab, setActiveTab] = usePersistedState(
    "vidal_products_tab",
    "geral"
  );
  const [chartTooltip, setChartTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
    type: null,
  });
  const chartRef = useRef(null);

  // Estados para dados da API
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productsData, setProductsData] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  // Estados para Reforma Tributária
  const [reformaData, setReformaData] = useState(null);
  const [reformaLoading, setReformaLoading] = useState(false);
  const [reformaError, setReformaError] = useState(null);
  const [selectedCenario, setSelectedCenario] = useState("2027");
  const [reformaSearchTerm, setReformaSearchTerm] = useState("");
  const [reformaSearchResults, setReformaSearchResults] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Infinite scroll states
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerTarget = useRef(null);

  // Obter projectId do localStorage
  const projectId = localStorage.getItem("vidal_selected_project");

  // Função para buscar produtos da API
  const fetchProducts = useCallback(async (page = 1, search = "", append = false) => {
    if (!projectId) {
      setError("Nenhum projeto selecionado");
      setLoading(false);
      return;
    }

    try {
      if (!append) {
        setLoading(true);
      }
      setError(null);

      const response = await api.get(`/products/${projectId}`, {
        params: {
          page,
          limit: 50,
          search,
          orderBy: "valorVendas",
          orderDir: "desc",
        },
      });

      if (response.data.success) {
        const newProducts = response.data.data.products || [];
        if (append) {
          setProductsData(prev => [...prev, ...newProducts]);
        } else {
          setProductsData(newProducts);
        }
        setPagination(response.data.data.pagination || {});
        setLastUpdate(new Date());
      } else {
        setError(response.data.error || "Erro ao buscar produtos");
      }
    } catch (err) {
      console.error("Erro ao buscar produtos:", err);
      setError(err.response?.data?.error || "Erro ao conectar com o servidor");
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [projectId]);

  // Função para buscar estatísticas
  const fetchStats = useCallback(async () => {
    if (!projectId) return;

    try {
      const response = await api.get(`/products/${projectId}/stats`);

      if (response.data.success) {
        setStatsData(response.data.data);
      }
    } catch (err) {
      console.error("Erro ao buscar estatísticas:", err);
    }
  }, [projectId]);

  // Função para buscar dados da Reforma Tributária
  const fetchReformaData = useCallback(async () => {
    if (!projectId) return;

    try {
      setReformaLoading(true);
      setReformaError(null);

      const response = await apiSped.get('/reforma/produtos/produto');

      if (response.data.success) {
        setReformaData(response.data.data);
        console.log('📊 Dados Reforma Tributária:', response.data.data);
      } else {
        setReformaError(response.data.error || 'Erro ao buscar dados da reforma');
      }
    } catch (err) {
      console.error('Erro ao buscar dados da reforma:', err);
      setReformaError(err.response?.data?.error || 'Erro ao conectar com o servidor SPED');
    } finally {
      setReformaLoading(false);
    }
  }, [projectId]);

  // Carregar dados ao montar o componente
  // Buscar dados da reforma quando a aba estiver ativa
  const reformaFetchedRef = useRef(false);
  useEffect(() => {
    if (activeTab === 'reforma' && !reformaFetchedRef.current) {
      reformaFetchedRef.current = true;
      fetchReformaData();
    }
  }, [activeTab, fetchReformaData]);

  useEffect(() => {
    fetchProducts(1, searchTerm, false);
    fetchStats();
  }, [fetchProducts, fetchStats]);

  // Estado para indicar se está buscando
  const [isSearching, setIsSearching] = useState(false);

  // Debounce para busca
  useEffect(() => {
    if (searchTerm !== undefined) {
      setIsSearching(true);
    }
    
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchProducts(1, searchTerm, false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Resetar isSearching quando o loading terminar
  useEffect(() => {
    if (!loading) {
      setIsSearching(false);
    }
  }, [loading]);

  // Mapa de nomes de meses completos
  const monthNamesComplete = {
    "Jan": "Janeiro", "Fev": "Fevereiro", "Mar": "Março", "Abr": "Abril",
    "Mai": "Maio", "Jun": "Junho", "Jul": "Julho", "Ago": "Agosto",
    "Set": "Setembro", "Out": "Outubro", "Nov": "Novembro", "Dez": "Dezembro"
  };

  // Dados mensais para o gráfico - usando dados da API
  const monthlyChartData = useMemo(() => {
    // Se temos dados da API, usar eles
    if (statsData?.chartData && statsData.chartData.length > 0) {
      const allData = statsData.chartData;
      
      // Encontrar o ano principal (o ano com mais dados/maior valor)
      const yearTotals = {};
      allData.forEach((item) => {
        if (item.ano) {
          if (!yearTotals[item.ano]) {
            yearTotals[item.ano] = { count: 0, total: 0 };
          }
          yearTotals[item.ano].count += 1;
          yearTotals[item.ano].total += (item.vendas || 0) + (item.compras || 0);
        }
      });
      
      // Pegar o ano com maior movimentação
      const mainYear = Object.entries(yearTotals)
        .sort((a, b) => b[1].total - a[1].total)[0]?.[0];
      
      if (!mainYear) return [];
      
      // Filtrar apenas dados do ano principal
      const yearData = allData.filter((item) => item.ano === parseInt(mainYear));
      
      // Mapear para o formato do gráfico
      return yearData.map((item) => ({
        mes: item.mesNome || item.mes,
        mesNome: monthNamesComplete[item.mesNome] || item.mesNome || item.mes,
        mesIndex: item.mesIndex,
        ano: item.ano,
        compras: item.compras || 0,
        vendas: item.vendas || 0,
        impostos: item.impostos || 0,
      })).sort((a, b) => a.mesIndex - b.mesIndex);
    }
    
    // Fallback: dados vazios se não tiver dados da API
    return [];
  }, [statsData]);

  // Estado para o mês selecionado no hover
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Atualizar selectedMonth quando os dados chegarem
  useEffect(() => {
    if (monthlyChartData.length > 0 && !selectedMonth) {
      setSelectedMonth(monthlyChartData[monthlyChartData.length - 1]);
    }
  }, [monthlyChartData, selectedMonth]);

  // Dados de devoluções memorizados (não muda a cada render)
  const produtosComDevolucoes = useMemo(() => {
    return [...productsData]
      .map((p, index) => ({
        ...p,
        // Usar índice como seed para gerar valor "aleatório" mas consistente
        devolucoes: ((index * 17 + 7) % 50) + 1,
      }))
      .sort((a, b) => b.devolucoes - a.devolucoes)
      .slice(0, 10);
  }, [productsData]);


  // Calcular totais (usando statsData da API ou fallback para productsData)
  const totals = useMemo(() => {
    if (statsData?.kpis) {
      return {
        produtos: statsData.kpis.produtosCadastrados || 0,
        compras: statsData.kpis.totalCompras || 0,
        vendas: statsData.kpis.totalVendas || 0,
        impostos: statsData.kpis.totalImpostos || 0,
      };
    }
    // Fallback para cálculo local
    return productsData.reduce(
      (acc, product) => ({
        produtos: acc.produtos + 1,
        compras: acc.compras + (product.valorCompras || 0),
        vendas: acc.vendas + (product.valorVendas || 0),
        impostos:
          acc.impostos +
          (product.icms || 0) +
          (product.pisCofins || 0) +
          (product.ibs || 0) +
          (product.cbs || 0) +
          (product.is || 0),
      }),
      { produtos: 0, compras: 0, vendas: 0, impostos: 0 }
    );
  }, [statsData, productsData]);

  // Calcular impostos detalhados
  const taxTotals = useMemo(() => {
    if (statsData?.impostos) {
      return {
        icms: statsData.impostos.icms || 0,
        pisCofins: statsData.impostos.pisCofins || 0,
        ibs: statsData.impostos.ibs || 0,
        cbs: statsData.impostos.cbs || 0,
        is: statsData.impostos.is || 0,
      };
    }
    // Fallback para cálculo local
    return productsData.reduce(
      (acc, product) => ({
        icms: acc.icms + (product.icms || 0),
        pisCofins: acc.pisCofins + (product.pisCofins || 0),
        ibs: acc.ibs + (product.ibs || 0),
        cbs: acc.cbs + (product.cbs || 0),
        is: acc.is + (product.is || 0),
      }),
      { icms: 0, pisCofins: 0, ibs: 0, cbs: 0, is: 0 }
    );
  }, [statsData, productsData]);

  // Processar categorias (usando productsData da API)
  const processCategories = useCallback(() => {
    const categoriesMap = new Map();
    productsData.forEach((product) => {
      // product.categoria é o código NCM de 2 dígitos (ex: "27" para Combustíveis)
      const ncmCode = product.categoria || "99";
      const categoria = NCM_SETOR_MAP[ncmCode] || NCM_SETOR_MAP["99"] || "Outros";
      if (!categoriesMap.has(categoria)) {
        categoriesMap.set(categoria, {
          name: categoria,
          count: 0,
          faturamento: 0,
        });
      }
      const categoryData = categoriesMap.get(categoria);
      categoryData.count += 1;
      categoryData.faturamento += product.valorVendas || 0;
    });
    const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
    const categories = Array.from(categoriesMap.values()).map(
      (category, index) => ({
        ...category,
        color: colors[index % colors.length],
        percentage: 0,
      })
    );
    const totalProducts = categories.reduce((sum, cat) => sum + cat.count, 0);
    categories.forEach((category) => {
      category.percentage =
        totalProducts > 0
          ? ((category.count / totalProducts) * 100).toFixed(1)
          : 0;
    });
    return categories.sort((a, b) => b.count - a.count);
  }, [productsData]);

  const categories = processCategories();

  // Produtos filtrados (a filtragem é feita pela API, aqui só usamos os dados)
  const filteredProducts = productsData;
  
  // Check if there are more pages to load from API
  const hasMore = pagination.page < pagination.totalPages;
  
  // Infinite scroll observer - loads next page from API
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !loading) {
          setIsLoadingMore(true);
          const nextPage = pagination.page + 1;
          fetchProducts(nextPage, searchTerm, true);
        }
      },
      { threshold: 0.1 }
    );
    
    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }
    
    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, isLoadingMore, loading, pagination.page, searchTerm, fetchProducts]);

  // Calcular impostos de um produto
  const calculateTotalTaxes = (product) => {
    return (
      (product.icms || 0) + (product.pisCofins || 0) + (product.ibs || 0) + (product.cbs || 0) + (product.is || 0)
    );
  };

  // Função para atualizar dados
  const handleRefresh = () => {
    fetchProducts(1, searchTerm, false);
    fetchStats();
    if (activeTab === 'reforma') {
      setReformaData(null); // Força recarregar dados da reforma
    }
  };

  // Calcular KPIs da Reforma Tributária
  const reformaKpis = useMemo(() => {
    if (!reformaData?.resumo) {
      return {
        valorMercadorias: 0,
        valorVendas: 0,
        valorCompras: 0,
        totalFornecedores: 0,
        totalProdutos: 0,
        aliquotaEfetiva: 0,
        debitosAtuais: 0,
        icms: 0,
        cofins: 0,
        pis: 0,
        ipi: 0,
        ivaDual: 0,
        cbs: 0,
        ibs: 0,
        aliquotaCBS: 0,
        aliquotaIBS: 0,
        impacto: 0,
        variacaoPercentual: 0,
        categorias: [],
        cronograma: [],
      };
    }

    const resumo = reformaData.resumo;
    const cenarioAno = parseInt(selectedCenario);
    const categorias = reformaData.categorias || [];
    
    // Encontrar dados do cenário selecionado no cronograma
    const cronogramaItem = resumo.cronogramaResumo?.find(c => c.ano === cenarioAno) || {};
    
    // Usar os mesmos valores de impostos da aba Geral (statsData)
    // IMPORTANTE: Usar DÉBITOS (impostos sobre vendas), não créditos
    const icms = statsData?.impostos?.icms || 0;
    const pisCofins = statsData?.impostos?.pisCofins || 0;
    const ipi = statsData?.impostos?.is || 0;
    
    // Separar PIS e COFINS (PIS = 1.65%, COFINS = 7.6% do total 9.25%)
    const pis = pisCofins * (1.65 / 9.25);
    const cofins = pisCofins * (7.6 / 9.25);
    
    // Total de débitos atuais (impostos sobre vendas)
    const debitosAtuais = icms + pisCofins + ipi;
    
    // Calcular valor em vendas (base para cálculo do IVA Dual)
    const valorVendas = statsData?.kpis?.totalVendas || resumo.totalVendas || resumo.totalAquisicoes || 0;
    const valorCompras = statsData?.kpis?.totalCompras || resumo.totalCompras || (resumo.totalAquisicoes || 0);
    
    // Obter alíquotas do cenário selecionado
    const aliquotaCBS = cronogramaItem.aliquotaCBS || 0.088; // Default 8.8%
    const aliquotaIBS = cronogramaItem.aliquotaIBS || 0.001; // Default 0.1%
    
    // CÁLCULO CORRETO: CBS e IBS sobre VENDAS (débitos)
    const cbsProjetado = valorVendas * aliquotaCBS;
    const ibsProjetado = valorVendas * aliquotaIBS;
    const ivaDualTotal = cbsProjetado + ibsProjetado;
    
    // Impacto da reforma: diferença entre IVA Dual e débitos atuais
    const impacto = ivaDualTotal - debitosAtuais;
    const variacaoPercentual = debitosAtuais > 0 ? ((impacto / debitosAtuais) * 100) : 0;

    return {
      valorMercadorias: resumo.totalAquisicoes || 0,
      valorVendas: valorVendas,
      valorCompras: valorCompras,
      totalFornecedores: categorias.length,
      totalProdutos: resumo.totalCategorias || 0,
      aliquotaEfetiva: resumo.aliquotaMediaIVADual2033 || 0,
      debitosAtuais: debitosAtuais,
      icms: icms,
      cofins: cofins,
      pis: pis,
      ipi: ipi,
      ivaDual: ivaDualTotal,
      cbs: cbsProjetado,
      ibs: ibsProjetado,
      aliquotaCBS: aliquotaCBS * 100,
      aliquotaIBS: aliquotaIBS * 100,
      impacto: impacto,
      variacaoPercentual: variacaoPercentual,
      categorias: categorias,
      cronograma: resumo.cronogramaResumo || [],
    };
  }, [reformaData, selectedCenario, statsData]);

  // Buscar produtos via API quando houver termo de busca na aba Reforma
  useEffect(() => {
    if (activeTab !== 'reforma') return;
    
    const searchProducts = async () => {
      if (!reformaSearchTerm.trim()) {
        setReformaSearchResults([]);
        return;
      }

      try {
        const response = await api.get(`/products/${projectId}`, {
          params: {
            page: 1,
            limit: 100,
            search: reformaSearchTerm,
            orderBy: "valorVendas",
            orderDir: "desc",
          },
        });

        if (response.data.success) {
          setReformaSearchResults(response.data.data.products || []);
        }
      } catch (err) {
        console.error("Erro ao buscar produtos na reforma:", err);
      }
    };

    const timeoutId = setTimeout(searchProducts, 300);
    return () => clearTimeout(timeoutId);
  }, [reformaSearchTerm, activeTab, projectId]);

  // Processar produtos para a tabela de reforma com ordenação e cálculo de CBS/IBS
  const reformaProductsData = useMemo(() => {
    // Usar resultados da busca se houver termo de busca, senão usar productsData
    const sourceData = reformaSearchTerm.trim() ? reformaSearchResults : productsData;
    
    if (!sourceData.length) return [];
    
    // Obter alíquotas do cenário selecionado
    const cenarioAno = parseInt(selectedCenario);
    const cronogramaItem = reformaKpis.cronograma?.find(c => c.ano === cenarioAno) || {};
    const aliquotaCBS = cronogramaItem.aliquotaCBS || 0.088;
    const aliquotaIBS = cronogramaItem.aliquotaIBS || 0.001;
    
    return sourceData
      .map(product => {
        // Calcular impostos atuais
        const icms = product.icms || 0;
        const pisCofins = product.pisCofins || 0;
        const pis = pisCofins * (1.65 / 9.25);
        const cofins = pisCofins * (7.6 / 9.25);
        const ipi = product.is || 0;
        const totalImpostosAtuais = icms + pisCofins + ipi;
        
        // Calcular CBS e IBS projetados baseado no valor de vendas
        // Se não houver vendas, usar valor de compras como base (para produtos que só têm entrada)
        const valorBase = product.valorVendas || product.valorCompras || 0;
        const cbsProjetado = valorBase * aliquotaCBS;
        const ibsProjetado = valorBase * aliquotaIBS;
        
        return {
          ...product,
          totalImpostosAtuais,
          pis,
          cofins,
          cbsProjetado,
          ibsProjetado,
        };
      })
      .filter(product => product.totalImpostosAtuais > 0 || product.cbsProjetado > 0)
      .sort((a, b) => b.totalImpostosAtuais - a.totalImpostosAtuais); // Ordenar por soma de impostos (maior primeiro)
  }, [productsData, reformaSearchResults, reformaSearchTerm, selectedCenario, reformaKpis.cronograma]);

  // Dados para gráfico de evolução temporal
  const evolucaoTemporalData = useMemo(() => {
    if (!reformaKpis.cronograma?.length) return [];
    
    return [
      { ano: "2024", valor: reformaKpis.debitosAtuais, label: "Atual" },
      ...reformaKpis.cronograma.map(item => ({
        ano: String(item.ano),
        valor: item.ganhoProjetado + reformaKpis.debitosAtuais,
        label: item.ano === 2027 ? "Início" : item.ano === 2033 ? "100%" : `${((item.ano - 2026) / 7 * 100).toFixed(0)}%`
      }))
    ];
  }, [reformaKpis]);

  // Dados para gráfico comparativo de tributos
  const comparativoTributosData = useMemo(() => {
    return [
      { name: "ICMS", atual: reformaKpis.icms, novo: 0 },
      { name: "PIS", atual: reformaKpis.pis, novo: 0 },
      { name: "COFINS", atual: reformaKpis.cofins, novo: 0 },
      { name: "IPI", atual: reformaKpis.ipi, novo: 0 },
      { name: "CBS", atual: 0, novo: reformaKpis.cbs },
      { name: "IBS", atual: 0, novo: reformaKpis.ibs },
    ];
  }, [reformaKpis]);

  // Preparar dados para o gráfico mensal (usado na aba estatísticas)
  const icmsShare = totals.impostos > 0 ? taxTotals.icms / totals.impostos : 0;
  const pisCofinsShare = totals.impostos > 0 ? taxTotals.pisCofins / totals.impostos : 0;

  const monthlyDataProcessed = useMemo(() => {
    if (!monthlyChartData || monthlyChartData.length === 0) return [];
    
    return monthlyChartData.map(d => ({
        month: d.mes,
        compras: d.compras,
        vendas: d.vendas,
        impostos: d.impostos,
        detalhesImpostos: {
          icms: d.impostos * icmsShare,
          pisCofins: d.impostos * pisCofinsShare
        }
      }));
  }, [monthlyChartData, icmsShare, pisCofinsShare]);

  const maxValProcessed = useMemo(() => {
    if (monthlyDataProcessed.length === 0) return 100;
    return Math.max(
        ...monthlyDataProcessed.flatMap((d) => [d.compras, d.vendas, d.impostos])
      );
  }, [monthlyDataProcessed]);

  // Preparar dados de categorias (usando statsData do backend se disponível, ou fallback para productsData)
  const categoriesProcessed = useMemo(() => {
    let rawCategories = [];
    
    // Se o backend enviar categories (nova implementação), usa elas:
    if (statsData?.categories && Array.isArray(statsData.categories)) {
       rawCategories = statsData.categories.map(c => ({
          ncm: c.code,
          count: c.count,
          valor: c.valor,
          impostos: c.impostos || 0
       }));
    } else {
       // Fallback: calcula localmente baseado em productsData
       const map = new Map();
       productsData.forEach(p => {
          const ncm = p.categoria || "99";
          if (!map.has(ncm)) map.set(ncm, { ncm, count: 0, valor: 0, impostos: 0 });
          const d = map.get(ncm);
          d.count++;
          d.valor += p.valorVendas || 0;
       });
       rawCategories = Array.from(map.values());
    }

    // 1. Ordenar categorias mapeadas por valor
    rawCategories.sort((a, b) => b.valor - a.valor);

    // 2. Separar Top 6 e Calda Longa
    let finalCategories = [];
    let othersAccumulator = { ncm: "99", count: 0, valor: 0, impostos: 0 };
    
    if (rawCategories.length > 6) {
       finalCategories = rawCategories.slice(0, 6);
       const tail = rawCategories.slice(6);
       
       // Reduzir a cauda para "Outros"
       othersAccumulator = tail.reduce((acc, curr) => ({
          ncm: "99",
          count: acc.count + curr.count,
          valor: acc.valor + curr.valor,
          impostos: acc.impostos + (curr.impostos || 0)
       }), othersAccumulator);
    } else {
       finalCategories = rawCategories;
    }

    // 3. Calcular Residual (Valor KPI Total - Valor Mapeado Total)
    // Isso captura vendas que podem ter escapado da agregação de NCMs (ex: NCMs nulos ou fora do top 5000 do ES)
    const totalMappedValor = rawCategories.reduce((acc, c) => acc + c.valor, 0);
    const totalVendasKPI = statsData?.kpis?.totalVendas || 0;
    const residualValor = totalVendasKPI - totalMappedValor;

    // Se houver residual significativo, soma ao acumulador de "Outros"
    if (residualValor > 10) {
        othersAccumulator.valor += residualValor;
        // Não somamos count pois não sabemos quantos itens são, evita inflar contagem errada.
    }

    // 4. Adicionar "Outros" se tiver valor relevante (seja da cauda ou do residual)
    if (othersAccumulator.valor > 0) {
        finalCategories.push(othersAccumulator);
    }
    
    // Reordenar final caso o residual tenha tornado "Outros" muito grande (opcional, mas bom para o gráfico)
    // finalCategories.sort((a, b) => b.valor - a.valor); // Se quiser manter Outros sempre no final, comente isso.
    
    // Assegurar cores consistentes
    const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#64748b"];
    
    return finalCategories.map((cat, i) => ({
        name: cat.ncm === "99" ? "Outros" : (NCM_SETOR_MAP[cat.ncm] || "Outros"),
        count: cat.count,
        valor: cat.valor,
        impostos: cat.impostos || 0,
        color: cat.ncm === "99" ? "#94a3b8" : colors[i % colors.length] // Cor cinza fixa para Outros
    }));
  }, [statsData, productsData]);

  const totalProductsInCategories = useMemo(() => 
      categoriesProcessed.reduce((sum, cat) => sum + cat.count, 0)
  , [categoriesProcessed]);


  // Se nenhum projeto selecionado, mostrar mensagem
  if (!projectId) {
    return (
      <Container>
        <div className="header-info">
          <div className="header-details">
            <h3>Nenhum Projeto Selecionado</h3>
            <div className="header-meta">
              <span className="status-tag">Selecione um projeto para visualizar os produtos</span>
            </div>
          </div>
        </div>
      </Container>
    );
  }

  // Se um produto está selecionado, mostrar a tela de detalhes
  if (selectedProduct) {
    return (
      <ProductDetail
        produto={selectedProduct}
        projectId={projectId}
        onVoltar={() => setSelectedProduct(null)}
        initialTab={activeTab === "reforma" ? "reforma" : "geral"}
      />
    );
  }



  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-details">
          <h3>Dashboard de Produtos</h3>
          <div className="header-meta">
            <span className={`status-tag ${error ? 'error' : 'active'}`}>
              {error ? 'Erro' : 'Sistema Online'}
            </span>
            <span className="update-tag">
              {lastUpdate ? `Atualizado ${lastUpdate.toLocaleTimeString('pt-BR')}` : 'Carregando...'}
            </span>
            <button
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={loading}
              title="Atualizar dados"
            >
              <GoSync className={loading ? 'spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs - Geral / Estatísticas / Reforma Tributária */}
      <div className="tabs-header">
        <button
          className={`tab ${activeTab === "geral" ? "active" : ""}`}
          onClick={() => setActiveTab("geral")}
        >
          <BiSpreadsheet />
          Geral
        </button>
        <button
          className={`tab ${activeTab === "estatisticas" ? "active" : ""}`}
          onClick={() => setActiveTab("estatisticas")}
        >
          <HiOutlineChartBar />
          Estatísticas
        </button>
        <button
          className={`tab ${activeTab === "reforma" ? "active" : ""}`}
          onClick={() => setActiveTab("reforma")}
        >
          <GoLaw />
          Reforma Tributária
        </button>
      </div>

      {/* Empty State — nenhum SPED carregado */}
      {!loading && productsData.length === 0 && (
        <EmptyState screen="products" />
      )}

      {/* ========== TAB GERAL ========== */}
      {activeTab === "geral" && productsData.length > 0 && (
        <>
          {/* KPI Cards - 4 principais */}
          <div className="kpi-cards main-stats">
            <div className="kpi-card primary">
              <div className="card-content">
                <div className="thumb">
                  <GoPackage />
                </div>
                <div className="text">
                  <p>{totalProductsInCategories}</p>
                  <p>Produtos Vendidos</p>
                </div>
              </div>
            </div>

            <div className="kpi-card success">
              <div className="card-content">
                <div className="thumb">
                  <GoGraph />
                </div>
                <div className="text">
                  <p>{formatCompact(totals.compras)}</p>
                  <p>Total de Compras</p>
                </div>
              </div>
            </div>

            <div className="kpi-card warning">
              <div className="card-content">
                <div className="thumb">
                  <GoZap />
                </div>
                <div className="text">
                  <p>{formatCompact(totals.vendas)}</p>
                  <p>Total de Vendas</p>
                </div>
              </div>
            </div>

            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb">
                  <GoCalendar />
                </div>
                <div className="text">
                  <p>{formatCompact(totals.impostos)}</p>
                  <p>Total de Impostos</p>
                </div>
              </div>
            </div>
          </div>

          {/* KPI Cards - Impostos */}
          <ProductTaxKPIs taxTotals={taxTotals} />

          {/* Filtros */}
          <div className="filters">
            <div className="search-input-wrapper">
              <div className="search-icon">
                {isSearching ? (
                  <FiLoader className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <GoSearch />
                )}
              </div>
              <input
                type="text"
                placeholder="Buscar por nome ou código do produto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.6,
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                  title="Limpar busca"
                >
                  <GoAlert style={{ fontSize: '16px', transform: 'rotate(45deg)' }} />
                </button>
              )}
            </div>
            {searchTerm && !isSearching && (
              <div style={{
                fontSize: '0.85em',
                opacity: 0.7,
                marginTop: '8px',
                marginLeft: '4px'
              }}>
                {filteredProducts.length === 0 ? (
                  <span style={{ color: '#ef4444' }}>Nenhum produto encontrado</span>
                ) : (
                  <span>
                    {filteredProducts.length} produto{filteredProducts.length !== 1 ? 's' : ''} encontrado{filteredProducts.length !== 1 ? 's' : ''}
                    {pagination.total > filteredProducts.length && ` (${pagination.total} no total)`}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Conteúdo Principal */}
          <MainContent>
            {/* Lista de Produtos */}
            <div className="products-table">
              {/* Header da Tabela */}
              <div className="table-header">
                <div className="header-cell produto">Produto</div>
                <div className="header-cell">Compras</div>
                <div className="header-cell">Vendas</div>
                <div className="header-cell">Faturamento</div>
                <div className="header-cell">Impostos</div>
              </div>

              {/* Lista de Produtos */}
              <div className="table-body">
                {loading && productsData.length === 0 ? (
                  <EntityListSkeleton
                    rowCount={8}
                    columns={[
                      { width: "minmax(0, 1.8fr)" },
                      { width: "minmax(0, 0.7fr)" },
                      { width: "minmax(0, 0.7fr)" },
                      { width: "minmax(0, 0.8fr)" },
                      { width: "minmax(0, 0.8fr)" },
                    ]}
                  />
                ) : error ? (
                  <div className="error-state">
                    <GoAlert style={{ fontSize: '48px', marginBottom: '16px' }} />
                    <h4>Erro ao carregar produtos</h4>
                    <p>{error}</p>
                    <button className="empty-cta" onClick={handleRefresh}>
                      <GoSync /> Tentar novamente
                    </button>
                  </div>
                ) : productsData.length === 0 ? (
                  <EmptyState screen="products" />
                ) : (
                  <>
                    {filteredProducts.map((product, index) => {
                      const totalImpostos = (product.icms || 0) + (product.pisCofins || 0) + (product.ipi || 0) + (product.ibs || 0) + (product.cbs || 0) + (product.is || 0);
                      const sector = NCM_SETOR_MAP[product.categoria || "99"] || "Outros";
                      return (
                        <EntityListRow
                          key={`product-${index}`}
                          entity={product}
                          color={getTagColor(product.codigo)}
                          icon={GoPackage}
                          eyebrow={`${product.codigo} · ${product.ncm || "—"}`}
                          title={product.nome || `Produto ${product.codigo}`}
                          tags={[
                            { label: sector, color: getTagColor(product.codigo) },
                            { label: product.unidade || "UN", neutral: true },
                          ]}
                          columns={[
                            { width: "minmax(0, 1.8fr)" },
                            { width: "minmax(0, 0.7fr)", render: (p) => (p.valorCompras > 0 ? formatCompact(p.valorCompras) : "—") },
                            { width: "minmax(0, 0.7fr)", render: (p) => (p.valorVendas > 0 ? formatCompact(p.valorVendas) : "—") },
                            { width: "minmax(0, 0.8fr)", render: (p) => formatCompact(p.valorVendas || 0) },
                            { width: "minmax(0, 0.8fr)", render: () => formatCompact(totalImpostos) },
                          ]}
                          onClick={() => setSelectedProduct(product)}
                        />
                      );
                    })}
                    {hasMore && (
                      <div ref={observerTarget} style={{ padding: '20px', textAlign: 'center' }}>
                        {isLoadingMore && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                            <span style={{ fontSize: '0.85em', opacity: 0.7 }}>Carregando mais produtos...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Card de Categorias — Donut Compacto */}
            <div className="stats-section chart-card compact-categories">
              <div className="donut-wrapper">
                <div className="donut-header">
                  <h4>Categorias por Setor</h4>
                  <p>Participação percentual no faturamento</p>
                </div>
                <div className="donut-chart">
                  <svg width="180" height="180" viewBox="0 0 200 200">
                    {(() => {
                      const total = totalProductsInCategories;
                      if (total === 0) return null;
                      let currentAngle = -90;
                      const radius = 80;
                      const innerRadius = 50;
                      const cx = 100, cy = 100;
                      return categoriesProcessed.map((cat, index) => {
                        const percentage = cat.count / total;
                        const angle = percentage * 360;
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        const startRad = (startAngle * Math.PI) / 180;
                        const endRad = (endAngle * Math.PI) / 180;
                        const largeArc = angle > 180 ? 1 : 0;
                        const x1 = cx + radius * Math.cos(startRad);
                        const y1 = cy + radius * Math.sin(startRad);
                        const x2 = cx + radius * Math.cos(endRad);
                        const y2 = cy + radius * Math.sin(endRad);
                        const ix1 = cx + innerRadius * Math.cos(startRad);
                        const iy1 = cy + innerRadius * Math.sin(startRad);
                        const ix2 = cx + innerRadius * Math.cos(endRad);
                        const iy2 = cy + innerRadius * Math.sin(endRad);
                        const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;
                        const midAngle = startAngle + angle / 2;
                        const midRad = (midAngle * Math.PI) / 180;
                        const labelR = (radius + innerRadius) / 2;
                        const labelX = cx + labelR * Math.cos(midRad);
                        const labelY = cy + labelR * Math.sin(midRad);
                        currentAngle += angle;
                        return (
                          <g key={index}>
                            <path d={path} fill={cat.color} />
                            {percentage > 0.08 && (
                              <text
                                x={labelX}
                                y={labelY}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fill="#fff"
                                fontSize="11"
                                fontWeight="700"
                              >
                                {(percentage * 100).toFixed(0)}%
                              </text>
                            )}
                          </g>
                        );
                      });
                    })()}
                  </svg>
                  <div className="donut-center">
                    <span className="donut-total">
                      {totalProductsInCategories.toLocaleString('pt-BR')}
                    </span>
                    <span className="donut-label">Produtos</span>
                  </div>
                </div>
                <div className="categories-list">
                  {categoriesProcessed.map((cat, index) => {
                    const maxCount = Math.max(...categoriesProcessed.map(c => c.count));
                    return (
                      <div key={index} className="category-item">
                        <div className="category-header">
                          <div className="category-color" style={{ backgroundColor: cat.color }}></div>
                          <span className="category-name">{cat.name}</span>
                          <span className="category-count">{cat.count.toLocaleString('pt-BR')}</span>
                        </div>
                        <div className="category-bar">
                          <div
                            className="category-progress"
                            style={{ width: `${(cat.count / maxCount) * 100}%`, backgroundColor: cat.color }}
                          ></div>
                        </div>
                        <div className="category-value">{formatNumber(cat.valor)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </MainContent>
        </>
      )}

      {/* ========== TAB ESTATÍSTICAS ========== */}
      {activeTab === "estatisticas" && !error && productsData.length > 0 && (
        <div className="stats-tab">
          {/* Container para todas as linhas de gráficos */}
          <div className="charts-container">
            {/* Linha 1: Donut e Barras */}
            <div className="charts-row">
              {/* Card Donut Chart */}
              <div className="stats-section chart-card">
                <div className="section-header">
                  <GoStack />
                  <h4>Distribuição por Categoria</h4>
                </div>
                <div className="donut-wrapper">
                  <div className="donut-chart">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      {(() => {
                        const total = totalProductsInCategories;
                        
                        if (total === 0) return null;

                        let currentAngle = -90;
                        const radius = 80;
                        const innerRadius = 50;
                        const cx = 100,
                          cy = 100;

                        return categoriesProcessed.map((cat, index) => {
                          const percentage = cat.count / total;
                          const angle = percentage * 360;
                          const startAngle = currentAngle;
                          const endAngle = currentAngle + angle;

                          const startRad = (startAngle * Math.PI) / 180;
                          const endRad = (endAngle * Math.PI) / 180;
                          const largeArc = angle > 180 ? 1 : 0;

                          const x1 = cx + radius * Math.cos(startRad);
                          const y1 = cy + radius * Math.sin(startRad);
                          const x2 = cx + radius * Math.cos(endRad);
                          const y2 = cy + radius * Math.sin(endRad);
                          const ix1 = cx + innerRadius * Math.cos(startRad);
                          const iy1 = cy + innerRadius * Math.sin(startRad);
                          const ix2 = cx + innerRadius * Math.cos(endRad);
                          const iy2 = cy + innerRadius * Math.sin(endRad);

                          const path = `M ${ix1} ${iy1} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;

                          const midAngle = startAngle + angle / 2;
                          const midRad = (midAngle * Math.PI) / 180;
                          const labelR = (radius + innerRadius) / 2;
                          const labelX = cx + labelR * Math.cos(midRad);
                          const labelY = cy + labelR * Math.sin(midRad);

                          currentAngle += angle;

                          return (
                            <g key={index}>
                              <path d={path} fill={cat.color} />
                              {percentage > 0.08 && (
                                <text
                                  x={labelX}
                                  y={labelY}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fill="#fff"
                                  fontSize="11"
                                  fontWeight="600"
                                >
                                  {(percentage * 100).toFixed(0)}%
                                </text>
                              )}
                            </g>
                          );
                        });
                      })()}
                    </svg>
                    <div className="donut-center">
                      <span className="donut-total">
                        {totalProductsInCategories.toLocaleString('pt-BR')}
                      </span>
                      <span className="donut-label">Produtos Vendidos</span>
                    </div>
                  </div>
                  <div className="categories-list">
                    {categoriesProcessed.map((cat, index) => {
                        const maxCount = Math.max(...categoriesProcessed.map(c => c.count));
                        return (
                        <div key={index} className="category-item">
                          <div className="category-header">
                            <div
                              className="category-color"
                              style={{ backgroundColor: cat.color }}
                            ></div>
                            <span className="category-name">{cat.name}</span>
                            <span className="category-count">{cat.count.toLocaleString('pt-BR')} itens</span>
                          </div>
                          <div className="category-bar">
                            <div
                              className="category-progress"
                              style={{
                                width: `${(cat.count / maxCount) * 100}%`,
                                backgroundColor: cat.color,
                              }}
                            ></div>
                          </div>
                          <div className="category-value">
                            {formatNumber(cat.valor)}
                          </div>
                        </div>
                      ); }
                    )}
                  </div>
                </div>
              </div>

              {/* Card Gráfico de Barras */}
              <div
                className="stats-section chart-card"
                style={{ position: "relative" }}
              >
                <div className="section-header">
                  <GoCalendar />
                  <h4>Análise Mensal</h4>
                </div>
                <div
                  className="bar-chart-wrapper"
                  ref={chartRef}
                  onMouseLeave={() => setChartTooltip({ ...chartTooltip, visible: false })}
                >
                  {monthlyDataProcessed.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: theme.textSecondary }}>
                      Nenhum dado mensal disponível.
                    </div>
                  ) : (
                    <>
                      <div className="chart-legend">
                        <div className="legend-item">
                          <div
                            className="legend-color"
                            style={{ backgroundColor: "#3b82f6" }}
                          ></div>
                          <span>Compras</span>
                        </div>
                        <div className="legend-item">
                          <div
                            className="legend-color"
                            style={{ backgroundColor: "#10b981" }}
                          ></div>
                          <span>Vendas</span>
                        </div>
                        <div className="legend-item">
                          <div
                            className="legend-color"
                            style={{ backgroundColor: "#ef4444" }}
                          ></div>
                          <span>Impostos</span>
                        </div>
                      </div>
                      <svg
                        width="100%"
                        height="100%"
                        preserveAspectRatio="none"
                        viewBox={`0 0 ${monthlyDataProcessed.length * 66 + 40} 240`}
                      >
                        {monthlyDataProcessed.map((data, i) => {
                          const chartHeight = 200;
                          const barWidth = 18;
                          const groupWidth = 66;
                          const x = i * groupWidth + 20;
                          
                          const comprasH = (data.compras / maxValProcessed) * chartHeight;
                          const vendasH = (data.vendas / maxValProcessed) * chartHeight;
                          const impostosH = (data.impostos / maxValProcessed) * chartHeight;

                          const handleBarHover = (e, type, barData) => {
                            setChartTooltip({
                              visible: true,
                              x: e.clientX,
                              y: e.clientY,
                              data: barData,
                              type: type,
                            });
                          };

                          return (
                            <g key={i}>
                              <rect
                                x={x}
                                y={chartHeight - comprasH}
                                width={barWidth}
                                height={comprasH}
                                fill="#3b82f6"
                                rx="3"
                                style={{ cursor: "pointer" }}
                                onMouseEnter={(e) =>
                                  handleBarHover(e, "compras", {
                                    month: data.month,
                                    value: data.compras,
                                  })
                                }
                                onMouseMove={(e) =>
                                  handleBarHover(e, "compras", {
                                    month: data.month,
                                    value: data.compras,
                                  })
                                }
                              />
                              <rect
                                x={x + barWidth + 4}
                                y={chartHeight - vendasH}
                                width={barWidth}
                                height={vendasH}
                                fill="#10b981"
                                rx="3"
                                style={{ cursor: "pointer" }}
                                onMouseEnter={(e) =>
                                  handleBarHover(e, "vendas", {
                                    month: data.month,
                                    value: data.vendas,
                                  })
                                }
                                onMouseMove={(e) =>
                                  handleBarHover(e, "vendas", {
                                    month: data.month,
                                    value: data.vendas,
                                  })
                                }
                              />
                              <rect
                                x={x + (barWidth + 4) * 2}
                                y={chartHeight - impostosH}
                                width={barWidth}
                                height={impostosH}
                                fill="#ef4444"
                                rx="3"
                                style={{ cursor: "pointer" }}
                                onMouseEnter={(e) =>
                                  handleBarHover(e, "impostos", {
                                    month: data.month,
                                    total: data.impostos,
                                    detalhes: data.detalhesImpostos,
                                  })
                                }
                                onMouseMove={(e) =>
                                  handleBarHover(e, "impostos", {
                                    month: data.month,
                                    total: data.impostos,
                                    detalhes: data.detalhesImpostos,
                                  })
                                }
                              />
                              <text
                                x={x + groupWidth / 2 - 6}
                                y={chartHeight + 20}
                                fontSize="10"
                                fill="#64748b"
                                textAnchor="middle"
                              >
                                {data.month}
                              </text>
                            </g>
                          );
                        })}
                      </svg>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Linha 2: Rankings */}
            <div className="rankings-row">
              {/* Top 5 Produtos por Faturamento */}
              <div className="ranking-card">
                <div className="ranking-header">
                  <GoGraph />
                  <h4>Produtos por Faturamento</h4>
                </div>
                <div className="ranking-list">
                  {[...productsData]
                    .sort((a, b) => (b.valorVendas || 0) - (a.valorVendas || 0))
                    .slice(0, 5)
                    .map((product, index) => (
                      <div key={product.id} className="ranking-item">
                        <div className="ranking-thumb faturamento">
                          <GoStack />
                        </div>
                        <div className="ranking-info">
                          <span className="ranking-name">{product.nome}</span>
                          <div className="ranking-tags">
                            <span className="category-tag">
                              {product.categoria}
                            </span>
                            <span className="code-tag">{product.codigo}</span>
                          </div>
                        </div>
                        <span className="ranking-value faturamento">
                          {formatNumber(product.valorVendas)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Top 10 Produtos com Maior Margem */}
              <div className="ranking-card">
                <div className="ranking-header">
                  <GoArrowUp />
                  <h4>Maior Margem de Lucro</h4>
                </div>
                <div className="ranking-list">
                  {[...productsData]
                    .map((p) => ({
                      ...p,
                      margem:
                        p.valorVendas && p.valorCompras
                          ? ((p.valorVendas - p.valorCompras) / p.valorVendas) *
                            100
                          : 0,
                    }))
                    .sort((a, b) => b.margem - a.margem)
                    .slice(0, 10)
                    .map((product, index) => (
                      <div key={product.id} className="ranking-item">
                        <div className="ranking-thumb margem">
                          <GoArrowUp />
                        </div>
                        <div className="ranking-info">
                          <span className="ranking-name">{product.nome}</span>
                          <div className="ranking-tags">
                            <span className="category-tag">
                              {product.categoria}
                            </span>
                          </div>
                        </div>
                        <span className="ranking-value margem">
                          {product.margem.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Top 10 Produtos Mais Devolvidos */}
              <div className="ranking-card">
                <div className="ranking-header">
                  <GoArrowDown />
                  <h4>Mais Devolvidos</h4>
                </div>
                <div className="ranking-list">
                  {produtosComDevolucoes.map((product) => (
                    <div key={product.id} className="ranking-item">
                      <div className="ranking-thumb devolucao">
                        <GoArrowDown />
                      </div>
                      <div className="ranking-info">
                        <span className="ranking-name">{product.nome}</span>
                        <div className="ranking-tags">
                          <span className="category-tag">
                            {product.categoria}
                          </span>
                        </div>
                      </div>
                      <span className="ranking-value devolucao">
                        {product.devolucoes} un
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Linha 3: KPIs Executivos */}
            <div className="executive-kpis-row">
              <div className="exec-kpi-card">
                <div className="kpi-icon blue">
                  <GoGraph />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">{formatNumber(totals.vendas).replace('R$ ', '')}</span>
                  <span className="kpi-label">Faturamento Total</span>
                  <span className="kpi-change neutral">
                     Acumulado
                  </span>
                </div>
              </div>
              <div className="exec-kpi-card">
                <div className="kpi-icon green">
                  <GoArrowUp />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">
                    {totals.vendas > 0 
                      ? (( (totals.vendas - totals.compras) / totals.vendas ) * 100).toFixed(1) 
                      : 0}%
                  </span>
                  <span className="kpi-label">Margem Bruta</span>
                  <span className="kpi-change positive">
                    Média Geral
                  </span>
                </div>
              </div>
              <div className="exec-kpi-card">
                <div className="kpi-icon orange">
                  <GoPackage />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">{totals.produtos}</span>
                  <span className="kpi-label">Produtos Ativos</span>
                  <span className="kpi-change neutral">Total Cadastrado</span>
                </div>
              </div>
              <div className="exec-kpi-card">
                <div className="kpi-icon red">
                  <GoLaw />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">{formatNumber(totals.impostos).replace('R$ ', '')}</span>
                  <span className="kpi-label">Carga Tributária</span>
                  <span className="kpi-change negative">
                    {totals.vendas > 0 ? ((totals.impostos / totals.vendas) * 100).toFixed(1) : 0}% da Receita
                  </span>
                </div>
              </div>
              <div className="exec-kpi-card">
                <div className="kpi-icon purple">
                  <GoCreditCard />
                </div>
                <div className="kpi-content">
                  <span className="kpi-value">{formatNumber(totals.compras).replace('R$ ', '')}</span>
                  <span className="kpi-label">Total Gasto (Compras)</span>
                  <span className="kpi-change neutral">Custo Total</span>
                </div>
              </div>
            </div>

            {/* Linha 4: Gráficos Avançados */}
            <div className="advanced-charts-row">
              {/* Gráfico de Evolução de Vendas */}
              <div className="advanced-chart-card">
                <div className="chart-header-advanced">
                  <div className="chart-title-group">
                    <HiOutlineChartBar className="chart-icon" />
                    <h4>Evolução de Vendas vs Compras</h4>
                  </div>
                  <div className="chart-period-selector">
                    <button className="period-btn active">12M</button>
                    <button className="period-btn">6M</button>
                    <button className="period-btn">3M</button>
                  </div>
                </div>
                <div className="chart-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={monthlyChartData.map(d => ({
                        mes: d.mes,
                        vendas: d.vendas,
                        compras: d.compras,
                        lucro: d.vendas - d.compras
                      }))}
                      margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                      barGap={2}
                    >
                      <defs>
                        <linearGradient
                          id="gradVendas"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#10b981"
                            stopOpacity={1}
                          />
                          <stop
                            offset="100%"
                            stopColor="#059669"
                            stopOpacity={1}
                          />
                        </linearGradient>
                        <linearGradient
                          id="gradCompras"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#3b82f6"
                            stopOpacity={1}
                          />
                          <stop
                            offset="100%"
                            stopColor="#2563eb"
                            stopOpacity={1}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.border}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="mes"
                        tick={{ fill: theme.text, fontSize: 11, opacity: 0.7 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: theme.text, fontSize: 10, opacity: 0.6 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) =>
                          `${(value / 1000000).toFixed(1)}M`
                        }
                        width={45}
                      />
                      <Tooltip
                        cursor={{ fill: theme.tableRowHover }}
                        contentStyle={{
                          background: theme.card,
                          border: `1px solid ${theme.border}`,
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        }}
                        formatter={(value) => [
                          `R$ ${(value / 1000000).toFixed(2)}M`,
                        ]}
                        labelStyle={{ color: theme.text, fontWeight: 600 }}
                        itemStyle={{ color: theme.text, opacity: 0.8 }}
                      />
                      <Bar
                        dataKey="vendas"
                        name="Vendas"
                        fill="url(#gradVendas)"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="compras"
                        name="Compras"
                        fill="url(#gradCompras)"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Gráfico de Composição Tributária */}
              <div className="advanced-chart-card">
                <div className="chart-header-advanced">
                  <div className="chart-title-group">
                    <GoLaw className="chart-icon" />
                    <h4>Composição Tributária</h4>
                  </div>
                  <span className="chart-subtitle">
                    Distribuição por tipo de imposto
                  </span>
                </div>
                <div className="chart-body tax-composition">
                  <div className="tax-donut-container">
                    <svg width="180" height="180" viewBox="0 0 180 180">
                      <defs>
                        <linearGradient
                          id="gradICMS"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#3b82f6" />
                          <stop offset="100%" stopColor="#1d4ed8" />
                        </linearGradient>
                        <linearGradient
                          id="gradPIS"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#10b981" />
                          <stop offset="100%" stopColor="#059669" />
                        </linearGradient>
                        <linearGradient
                          id="gradCOFINS"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#f59e0b" />
                          <stop offset="100%" stopColor="#d97706" />
                        </linearGradient>
                        <linearGradient
                          id="gradIPI"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#8b5cf6" />
                          <stop offset="100%" stopColor="#7c3aed" />
                        </linearGradient>
                      </defs>
                      {/* ICMS - 45% */}
                      <circle
                        cx="90"
                        cy="90"
                        r="70"
                        fill="none"
                        stroke="url(#gradICMS)"
                        strokeWidth="20"
                        strokeDasharray="198 440"
                        strokeDashoffset="0"
                        transform="rotate(-90 90 90)"
                      />
                      {/* PIS - 15% */}
                      <circle
                        cx="90"
                        cy="90"
                        r="70"
                        fill="none"
                        stroke="url(#gradPIS)"
                        strokeWidth="20"
                        strokeDasharray="66 440"
                        strokeDashoffset="-198"
                        transform="rotate(-90 90 90)"
                      />
                      {/* COFINS - 25% */}
                      <circle
                        cx="90"
                        cy="90"
                        r="70"
                        fill="none"
                        stroke="url(#gradCOFINS)"
                        strokeWidth="20"
                        strokeDasharray="110 440"
                        strokeDashoffset="-264"
                        transform="rotate(-90 90 90)"
                      />
                      {/* IPI - 15% */}
                      <circle
                        cx="90"
                        cy="90"
                        r="70"
                        fill="none"
                        stroke="url(#gradIPI)"
                        strokeWidth="20"
                        strokeDasharray="66 440"
                        strokeDashoffset="-374"
                        transform="rotate(-90 90 90)"
                      />
                      <text
                        x="90"
                        y="85"
                        textAnchor="middle"
                        fill={theme.text}
                        fontSize="20"
                        fontWeight="700"
                      >
                        {formatCompact(totals.impostos)}
                      </text>
                      <text
                        x="90"
                        y="105"
                        textAnchor="middle"
                        fill={theme.text}
                        fontSize="11"
                        opacity="0.6"
                      >
                        Total Impostos
                      </text>
                    </svg>
                  </div>
                  <div className="tax-legend-list">
                    <div className="tax-legend-item">
                      <div className="tax-color icms"></div>
                      <div className="tax-info">
                        <span className="tax-name">ICMS</span>
                        <span className="tax-percent">{totals.impostos > 0 ? ((taxTotals.icms / totals.impostos) * 100).toFixed(0) : 0}%</span>
                      </div>
                      <span className="tax-value">{formatCompact(taxTotals.icms)}</span>
                    </div>
                    <div className="tax-legend-item">
                      <div className="tax-color cofins"></div>
                      <div className="tax-info">
                        <span className="tax-name">PIS/COFINS</span>
                        <span className="tax-percent">{totals.impostos > 0 ? ((taxTotals.pisCofins / totals.impostos) * 100).toFixed(0) : 0}%</span>
                      </div>
                      <span className="tax-value">{formatCompact(taxTotals.pisCofins)}</span>
                    </div>
                    <div className="tax-legend-item">
                      <div className="tax-color ipi"></div>
                      <div className="tax-info">
                        <span className="tax-name">IPI</span>
                        <span className="tax-percent">{totals.impostos > 0 ? (((taxTotals.ipi || 0) / totals.impostos) * 100).toFixed(0) : 0}%</span>
                      </div>
                      <span className="tax-value">{formatCompact(taxTotals.ipi || 0)}</span>
                    </div>
                    <div className="tax-legend-item">
                      <div className="tax-color pis"></div>
                      <div className="tax-info">
                        <span className="tax-name">IBS</span>
                        <span className="tax-percent">{totals.impostos > 0 ? ((taxTotals.ibs / totals.impostos) * 100).toFixed(0) : 0}%</span>
                      </div>
                      <span className="tax-value">{formatCompact(taxTotals.ibs)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Linha 5: Tabela de Performance por Categoria */}
            <div className="category-performance-section">
              <div className="section-header-row">
                <div className="section-title">
                  <BiSpreadsheet className="section-icon" />
                  <h3>Performance por Categoria</h3>
                </div>
                <span className="section-subtitle">
                  Análise comparativa de métricas por segmento
                </span>
              </div>
              <div className="performance-table-container">
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th className="col-categoria">Categoria</th>
                      <th className="col-produtos">Produtos</th>
                      <th className="col-faturamento">Faturamento</th>
                      <th className="col-participacao">Participação</th>
                      <th className="col-margem">Margem Média</th>
                      <th className="col-impostos">Carga Tributária</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoriesProcessed.map((cat, index) => {
                      const totalVal = categoriesProcessed.reduce((a, b) => a + b.valor, 0);
                      const part = totalVal ? (cat.valor / totalVal) * 100 : 0;
                      return (
                      <tr key={index} className="data-row">
                        <td className="col-categoria">
                          <div
                            className="categoria-badge"
                            style={{
                              "--cat-color": cat.color,
                            }}
                          >
                            <div className="cat-dot"></div>
                            <span className="cat-name">{cat.name}</span>
                          </div>
                        </td>
                        <td className="col-produtos">
                          <span className="produtos-count">{cat.count.toLocaleString('pt-BR')}</span>
                        </td>
                        <td className="col-faturamento">
                          <span className="valor-destaque">
                             R$ {formatNumber(cat.valor)}
                          </span>
                        </td>
                        <td className="col-participacao">
                          <div className="participacao-bar-container">
                            <span className="participacao-value">{part.toFixed(1)}%</span>
                            <div
                                className="participacao-bar"
                                style={{
                                  width: `${part}%`,
                                  backgroundColor: cat.color,
                                }}
                              ></div>
                          </div>
                        </td>
                        <td className="col-margem">
                          <span className="trend positive">-</span>
                        </td>
                        <td className="col-impostos">
                          <span className="impostos-value">R$ {formatNumber(cat.impostos)}</span>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </div>


          </div>
        </div>
      )}

      {/* ========== TAB REFORMA TRIBUTÁRIA ========== */}
      {activeTab === "reforma" && (
        <div className="reforma-tab">
          {/* Loading State with Skeleton */}
          {reformaLoading && (
            <>
              {/* Skeleton para botões de cenário */}
              <div className="cenario-buttons">
                {Array.from({ length: 7 }).map((_, i) => (
                  <button 
                    key={`skeleton-btn-${i}`}
                    className="cenario-btn"
                    style={{ pointerEvents: 'none' }}
                  >
                    <span className="cenario-year" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '20px', width: '50px', display: 'inline-block' }}></span>
                    <span className="cenario-label" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '14px', width: '80px', display: 'inline-block', marginTop: '4px' }}></span>
                  </button>
                ))}
              </div>

              {/* Linha 1 - Visão Geral */}
              <div className="kpi-cards main-stats">
                {[
                  { icon: GoPackage, variant: 'primary' },
                  { icon: GoGraph, variant: 'warning' },
                  { icon: GoGraph, variant: 'info' },
                  { icon: GoGraph, variant: 'cte' }
                ].map((item, i) => (
                  <div key={`skeleton-main-${i}`} className={`kpi-card ${item.variant}`}>
                    <div className="card-content">
                      <div className="thumb">
                        <item.icon />
                      </div>
                      <div className="text">
                        <p style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '24px', width: '120px', marginBottom: '8px' }}></p>
                        <p style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '16px', width: '150px' }}></p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Linha 2 - Débitos Tributários Atuais */}
              <div className="kpi-cards secondary-stats">
                {[
                  { icon: GoCreditCard, variant: 'warning with-progress' },
                  { icon: GoLaw, variant: '' },
                  { icon: GoLaw, variant: 'nfe' },
                  { icon: GoLaw, variant: 'info' },
                  { icon: GoLaw, variant: 'cte' }
                ].map((item, i) => (
                  <div key={`skeleton-debitos-${i}`} className={`kpi-card compact ${item.variant}`}>
                    <div className="card-content">
                      <div className="thumb">
                        <item.icon />
                      </div>
                      <div className="text">
                        <p style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '20px', width: '100px', marginBottom: '6px' }}></p>
                        <p style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '14px', width: '80px' }}></p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Linha 3 - IVA Dual Projetado */}
              <div className="kpi-cards secondary-stats">
                {[
                  { icon: GoCreditCard, variant: 'success with-progress' },
                  { icon: GoLaw, variant: 'success' },
                  { icon: GoLaw, variant: 'info' },
                  { icon: GoLaw, variant: 'primary' },
                  { icon: GoLaw, variant: 'cte' }
                ].map((item, i) => (
                  <div key={`skeleton-iva-${i}`} className={`kpi-card compact ${item.variant}`}>
                    <div className="card-content">
                      <div className="thumb">
                        <item.icon />
                      </div>
                      <div className="text">
                        <p style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '20px', width: '100px', marginBottom: '6px' }}></p>
                        <p style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '14px', width: '80px' }}></p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Busca de Produtos Skeleton */}
              <div className="reforma-search">
                <div className="search-input-wrapper">
                  <GoSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Buscar produtos por código, nome ou NCM..."
                    className="search-input"
                    disabled
                    style={{ pointerEvents: 'none' }}
                  />
                </div>
              </div>

              {/* Gráficos Skeleton */}
              <div className="reforma-charts-grid">
                {/* Gráfico 1 - Comparativo de Tributos */}
                <div className="reforma-chart-card">
                  <div className="chart-header">
                    <div className="chart-title">
                      <GoGraph className="chart-icon" />
                      <span style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '18px', width: '200px', display: 'inline-block' }}></span>
                    </div>
                  </div>
                  <div className="chart-body" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'rgba(128, 128, 128, 0.1)', borderRadius: '8px', height: '250px', width: '90%' }}></div>
                  </div>
                </div>

                {/* Gráfico 2 - Evolução da Carga Tributária */}
                <div className="reforma-chart-card">
                  <div className="chart-header">
                    <div className="chart-title">
                      <GoGraph className="chart-icon" />
                      <span style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '18px', width: '250px', display: 'inline-block' }}></span>
                    </div>
                  </div>
                  <div className="chart-body" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'rgba(128, 128, 128, 0.1)', borderRadius: '8px', height: '250px', width: '90%' }}></div>
                  </div>
                </div>
              </div>

              {/* Tabela de Produtos Skeleton */}
              <div className="reforma-products-section">
                <div className="section-header-row">
                  <div className="section-title">
                    <BiSpreadsheet className="section-icon" />
                    <h3 style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '20px', width: '300px', display: 'inline-block' }}></h3>
                  </div>
                </div>

                <div className="reforma-products-table">
                  <div className="table-header">
                    <div className="header-cell produto" style={{ background: 'rgba(128, 128, 128, 0.15)', borderRadius: '4px', height: '16px' }}></div>
                    <div className="header-cell" style={{ background: 'rgba(128, 128, 128, 0.15)', borderRadius: '4px', height: '16px' }}></div>
                    <div className="header-cell" style={{ background: 'rgba(128, 128, 128, 0.15)', borderRadius: '4px', height: '16px' }}></div>
                    <div className="header-cell" style={{ background: 'rgba(128, 128, 128, 0.15)', borderRadius: '4px', height: '16px' }}></div>
                    <div className="header-cell" style={{ background: 'rgba(128, 128, 128, 0.15)', borderRadius: '4px', height: '16px' }}></div>
                    <div className="header-cell" style={{ background: 'rgba(128, 128, 128, 0.15)', borderRadius: '4px', height: '16px' }}></div>
                  </div>
                  <div className="table-body">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div
                        key={`skeleton-product-${index}`}
                        className="product-row"
                        style={{ pointerEvents: 'none' }}
                      >
                        <div className="product-info">
                          <span className="product-code" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '14px', width: '80px', display: 'inline-block' }}></span>
                          <span className="product-name" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '16px', width: '200px', display: 'inline-block', marginTop: '4px' }}></span>
                        </div>
                        <div className="tax-value" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '16px', width: '80px' }}></div>
                        <div className="tax-value" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '16px', width: '80px' }}></div>
                        <div className="tax-value" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '16px', width: '80px' }}></div>
                        <div className="tax-value" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '16px', width: '80px' }}></div>
                        <div className="tax-value" style={{ background: 'rgba(128, 128, 128, 0.2)', borderRadius: '4px', height: '16px', width: '80px' }}></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Error State */}
          {reformaError && !reformaLoading && (
            <div className="error-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
              <GoAlert style={{ fontSize: '48px', color: '#ef4444', marginBottom: '16px' }} />
              <p style={{ color: theme.text, marginBottom: '8px' }}>{reformaError}</p>
              <button onClick={fetchReformaData} style={{ padding: '8px 16px', borderRadius: '8px', background: theme.primary, color: '#fff', border: 'none', cursor: 'pointer' }}>
                Tentar Novamente
              </button>
            </div>
          )}

          {/* Content */}
          {!reformaLoading && !reformaError && (
            <>
          {/* Botões de Cenário */}
          <div className="cenario-buttons">
            <button 
              className={`cenario-btn ${selectedCenario === "2027" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2027")}
            >
              <span className="cenario-year">2027</span>
              <span className="cenario-label">Início IVA Dual</span>
            </button>
            <button 
              className={`cenario-btn ${selectedCenario === "2028" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2028")}
            >
              <span className="cenario-year">2028</span>
              <span className="cenario-label">Transição</span>
            </button>
            <button 
              className={`cenario-btn ${selectedCenario === "2029" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2029")}
            >
              <span className="cenario-year">2029</span>
              <span className="cenario-label">Transição 40%</span>
            </button>
            <button 
              className={`cenario-btn ${selectedCenario === "2030" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2030")}
            >
              <span className="cenario-year">2030</span>
              <span className="cenario-label">Transição 60%</span>
            </button>
            <button 
              className={`cenario-btn ${selectedCenario === "2031" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2031")}
            >
              <span className="cenario-year">2031</span>
              <span className="cenario-label">Transição 80%</span>
            </button>
            <button 
              className={`cenario-btn ${selectedCenario === "2032" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2032")}
            >
              <span className="cenario-year">2032</span>
              <span className="cenario-label">Transição 100%</span>
            </button>
            <button 
              className={`cenario-btn ${selectedCenario === "2033" ? "active" : ""}`}
              onClick={() => setSelectedCenario("2033")}
            >
              <span className="cenario-year">2033</span>
              <span className="cenario-label">IVA Dual Pleno</span>
            </button>
          </div>

          {/* Linha 1 - Visão Geral */}
          <div className="kpi-cards main-stats">
            <div className="kpi-card primary">
              <div className="card-content">
                <div className="thumb">
                  <GoPackage />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.valorMercadorias)}</p>
                  <p>Valor Total de Mercadorias</p>
                </div>
              </div>
            </div>

            <div className="kpi-card warning">
              <div className="card-content">
                <div className="thumb">
                  <GoGraph />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.valorVendas)}</p>
                  <p>Valor em Vendas</p>
                </div>
              </div>
            </div>

            <div className="kpi-card info">
              <div className="card-content">
                <div className="thumb">
                  <GoGraph />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.valorCompras)}</p>
                  <p>Valor em Compras</p>
                </div>
              </div>
            </div>

            <div className="kpi-card cte">
              <div className="card-content">
                <div className="thumb">
                  <GoGraph />
                </div>
                <div className="text">
                  <p>{(reformaKpis.aliquotaCBS + reformaKpis.aliquotaIBS).toFixed(2)}%</p>
                  <p>Alíquota Efetiva ({selectedCenario})</p>
                </div>
              </div>
            </div>
          </div>

          {/* Linha 2 - Débitos Tributários Atuais */}
          <div className="kpi-cards secondary-stats">
            <div className="kpi-card compact warning with-progress">
              <div className="card-content">
                <div className="thumb">
                  <GoCreditCard />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.debitosAtuais)}</p>
                  <p>Créditos Tributários Atuais</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.icms)}</p>
                  <p>ICMS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact nfe">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.cofins)}</p>
                  <p>COFINS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact info">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.pis)}</p>
                  <p>PIS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact cte">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.ipi)}</p>
                  <p>IPI</p>
                </div>
              </div>
            </div>
          </div>

          {/* Linha 3 - IVA Dual Projetado */}
          <div className="kpi-cards secondary-stats">
            <div className="kpi-card compact success with-progress">
              <div className="card-content">
                <div className="thumb">
                  <GoCreditCard />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.ivaDual)}</p>
                  <p>IVA Dual Projetado ({selectedCenario})</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact success">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.cbs)}</p>
                  <p>CBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact info">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{reformaKpis.aliquotaCBS.toFixed(2)}%</p>
                  <p>Alíquota CBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact primary">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{formatCurrency(reformaKpis.ibs)}</p>
                  <p>IBS</p>
                </div>
              </div>
            </div>

            <div className="kpi-card compact cte">
              <div className="card-content">
                <div className="thumb">
                  <GoLaw />
                </div>
                <div className="text">
                  <p>{reformaKpis.aliquotaIBS.toFixed(2)}%</p>
                  <p>Alíquota IBS</p>
                </div>
              </div>
            </div>
          </div>

          {/* Busca de Produtos */}
          <div className="reforma-search">
            <div className="search-input-wrapper">
              <GoSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar produtos por código, nome ou NCM..."
                className="search-input"
                value={reformaSearchTerm}
                onChange={(e) => setReformaSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Tabela de Produtos com Impostos + Card Impacto */}
          <div className="reforma-content-row">
            <div className="reforma-products-table">
              <div className="table-header">
                <div className="header-cell produto">Produto</div>
                <div className="header-cell">ICMS</div>
                <div className="header-cell">COFINS</div>
                <div className="header-cell">PIS</div>
                <div className="header-cell">IPI</div>
                <div className="header-cell">CBS</div>
                <div className="header-cell">IBS</div>
              </div>
              <div className="table-body">
                {reformaProductsData.map((product, index) => (
                  <div
                    key={product.id}
                    className="product-row"
                    style={{
                      "--product-color": [
                        "#3b82f6",
                        "#10b981",
                        "#f59e0b",
                        "#8b5cf6",
                        "#ef4444",
                      ][index % 5],
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="product-info">
                      <GoPackage
                        className="product-icon"
                        style={{ color: ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"][index % 5] }}
                      />
                      <div className="product-details">
                        <span className="product-code">{product.codigo}</span>
                        <span className="product-name">{product.nome}</span>
                      </div>
                    </div>
                    <div className="tax-value icms">
                      {formatNumber(product.icms || 0)}
                    </div>
                    <div className="tax-value cofins">
                      {formatNumber(product.cofins || 0)}
                    </div>
                    <div className="tax-value pis">
                      {formatNumber(product.pis || 0)}
                    </div>
                    <div className="tax-value ipi">
                      {formatNumber(product.is || 0)}
                    </div>
                    <div className="tax-value cbs">
                      {formatNumber(product.cbsProjetado || 0)}
                    </div>
                    <div className="tax-value ibs">
                      {formatNumber(product.ibsProjetado || 0)}
                    </div>
                  </div>
                ))}
                
                {/* Observer target for infinite scroll */}
                {hasMore && (
                  <div ref={observerTarget} style={{ padding: '20px', textAlign: 'center' }}>
                    {isLoadingMore && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                        <span>Carregando mais produtos...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Card Impacto da Reforma */}
            <div className="impacto-sidebar">
              {/* Card Principal - Impacto */}
              <div className="impacto-main-card">
                <div className="impacto-header">
                  <div className="impacto-icon">
                    <GoGraph />
                  </div>
                  <span className={`impacto-badge ${Math.abs(reformaKpis.impacto) > 50000 ? 'alto' : Math.abs(reformaKpis.impacto) > 10000 ? 'medio' : 'baixo'}`}>
                    {Math.abs(reformaKpis.impacto) > 50000 ? 'ALTO IMPACTO' : Math.abs(reformaKpis.impacto) > 10000 ? 'MÉDIO IMPACTO' : 'BAIXO IMPACTO'}
                  </span>
                </div>
                <span className="impacto-title">Impacto da Reforma</span>
                <span className="impacto-value">{formatCurrency(Math.abs(reformaKpis.impacto))}</span>
                <span className="impacto-desc">
                  {reformaKpis.impacto >= 0 ? 'Aumento tributário projetado' : 'Economia tributária projetada'}
                </span>
                <div className="impacto-variation">
                  {reformaKpis.impacto >= 0 ? <GoArrowUp /> : <GoArrowDown />}
                  <span>{reformaKpis.impacto >= 0 ? '+' : ''}{reformaKpis.variacaoPercentual.toFixed(1)}%</span>
                  <span className="variation-label">vs. sistema atual</span>
                </div>
              </div>

              {/* Card Comparativo */}
              <div className="impacto-compare-card">
                <span className="compare-title">Comparativo de Carga</span>
                <div className="compare-items">
                  <div className="compare-item">
                    <span className="compare-label">Sistema Atual</span>
                    <span className="compare-value atual">{formatCurrency(reformaKpis.debitosAtuais)}</span>
                  </div>
                  <div className="compare-item">
                    <span className="compare-label">IVA Dual ({selectedCenario})</span>
                    <span className="compare-value novo">{formatCurrency(reformaKpis.ivaDual)}</span>
                  </div>
                  <div className="compare-item highlight">
                    <span className="compare-label">Diferença</span>
                    <span className="compare-value diff">{reformaKpis.impacto >= 0 ? '+' : ''}{formatCurrency(reformaKpis.impacto)}</span>
                  </div>
                </div>
              </div>

              {/* Card Alíquotas */}
              <div className="impacto-aliquotas-card">
                <span className="aliquotas-title">Alíquotas Projetadas</span>
                <div className="aliquota-row">
                  <div className="aliquota-item">
                    <span className="aliquota-sigla">CBS</span>
                    <span className="aliquota-valor">{reformaKpis.aliquotaCBS.toFixed(2)}%</span>
                  </div>
                  <div className="aliquota-item">
                    <span className="aliquota-sigla">IBS</span>
                    <span className="aliquota-valor">{reformaKpis.aliquotaIBS.toFixed(2)}%</span>
                  </div>
                </div>
                <div className="aliquota-total">
                  <span>Alíquota Efetiva Total</span>
                  <span className="total-valor">{(reformaKpis.aliquotaCBS + reformaKpis.aliquotaIBS).toFixed(2)}%</span>
                </div>
              </div>

              {/* Card Timeline */}
              <div className="impacto-timeline-card">
                <span className="timeline-title">Cronograma de Transição</span>
                <div className="timeline-steps">
                  <div className="timeline-step active">
                    <span className="step-year">2027</span>
                    <span className="step-desc">Início</span>
                  </div>
                  <div className="timeline-step">
                    <span className="step-year">2029</span>
                    <span className="step-desc">40%</span>
                  </div>
                  <div className="timeline-step">
                    <span className="step-year">2033</span>
                    <span className="step-desc">100%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráficos de Barras */}
          <div className="reforma-charts-row">
            {/* Gráfico 1 - Comparativo de Tributos */}
            <div className="reforma-chart-card">
              <div className="chart-header">
                <div className="chart-title">
                  <HiOutlineChartBar className="chart-icon" />
                  <span>Comparativo de Tributos</span>
                </div>
                <span className="chart-subtitle">
                  Sistema Atual vs IVA Dual
                </span>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart
                    data={comparativoTributosData}
                    margin={{ top: 5, right: 5, left: 35, bottom: 20 }}
                    barGap={2}
                    barCategoryGap="15%"
                  >
                    <defs>
                      <linearGradient
                        id="gradientAtual"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#9ca3af" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#6b7280"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="gradientNovo"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#3b82f6"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <filter
                        id="shadow1"
                        x="-20%"
                        y="-20%"
                        width="140%"
                        height="140%"
                      >
                        <feDropShadow
                          dx="0"
                          dy="2"
                          stdDeviation="3"
                          floodColor="#3b82f6"
                          floodOpacity="0.3"
                        />
                      </filter>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{
                        fill: theme.text,
                        fontSize: 11,
                        fontWeight: 500,
                        opacity: 0.7,
                      }}
                      axisLine={false}
                      tickLine={false}
                      dy={5}
                    />
                    <YAxis
                      tick={{ fill: theme.text, fontSize: 10, opacity: 0.6 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) =>
                        value >= 1000000
                          ? `${(value / 1000000).toFixed(1)}M`
                          : value >= 1000
                          ? `${(value / 1000).toFixed(0)}K`
                          : value
                      }
                      width={40}
                    />
                    <Tooltip
                      cursor={{ fill: theme.tableRowHover }}
                      contentStyle={{
                        background: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        padding: "8px 12px",
                      }}
                      formatter={(value, name) => [
                        `R$ ${value.toLocaleString("pt-BR")}`,
                        name === "atual" ? "Sistema Atual" : "IVA Dual",
                      ]}
                      labelStyle={{
                        color: theme.text,
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                      itemStyle={{
                        color: theme.text,
                        opacity: 0.8,
                        fontSize: 12,
                      }}
                    />
                    <Bar
                      dataKey="atual"
                      name="atual"
                      fill="url(#gradientAtual)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                    />
                    <Bar
                      dataKey="novo"
                      name="novo"
                      fill="url(#gradientNovo)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-legend">
                <div className="legend-item">
                  <span
                    className="legend-dot"
                    style={{
                      background: "linear-gradient(180deg, #9ca3af, #6b7280)",
                    }}
                  ></span>
                  <span>Sistema Atual</span>
                </div>
                <div className="legend-item">
                  <span
                    className="legend-dot"
                    style={{
                      background: "linear-gradient(180deg, #60a5fa, #3b82f6)",
                    }}
                  ></span>
                  <span>IVA Dual (2027)</span>
                </div>
              </div>
            </div>

            {/* Gráfico 2 - Evolução da Carga Tributária */}
            <div className="reforma-chart-card">
              <div className="chart-header">
                <div className="chart-title">
                  <GoGraph className="chart-icon" />
                  <span>Evolução da Carga Tributária</span>
                </div>
                <span className="chart-subtitle">
                  Projeção por ano de transição
                </span>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={evolucaoTemporalData.length > 0 ? evolucaoTemporalData : [
                      { ano: "2024", valor: 0, label: "Atual" },
                      { ano: "2027", valor: 0, label: "Início" },
                      { ano: "2033", valor: 0, label: "100%" },
                    ]}
                    margin={{ top: 30, right: 30, left: 60, bottom: 20 }}
                  >
                    <defs>
                      <linearGradient
                        id="barGradient2024"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#9ca3af" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#6b7280"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2027"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#93c5fd" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#60a5fa"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2028"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#3b82f6"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2029"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#2563eb"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2030"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#1d4ed8"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2031"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#1d4ed8" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#1e40af"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2032"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#1e40af" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#1e3a8a"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="barGradient2033"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#1e3a8a" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#172554"
                          stopOpacity={1}
                        />
                      </linearGradient>
                      <filter
                        id="shadow2"
                        x="-20%"
                        y="-20%"
                        width="140%"
                        height="140%"
                      >
                        <feDropShadow
                          dx="0"
                          dy="3"
                          stdDeviation="4"
                          floodColor="#3b82f6"
                          floodOpacity="0.25"
                        />
                      </filter>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="ano"
                      tick={{
                        fill: theme.text,
                        fontSize: 12,
                        fontWeight: 500,
                        opacity: 0.7,
                      }}
                      axisLine={{
                        stroke: theme.border,
                        strokeWidth: 1,
                      }}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      tick={{ fill: theme.text, fontSize: 11, opacity: 0.6 }}
                      axisLine={{
                        stroke: theme.border,
                        strokeWidth: 1,
                      }}
                      tickLine={false}
                      tickFormatter={(value) =>
                        value >= 1000000
                          ? `R$ ${(value / 1000000).toFixed(1)}M`
                          : value >= 1000
                          ? `R$ ${(value / 1000).toFixed(0)}K`
                          : `R$ ${value}`
                      }
                      width={55}
                    />
                    <Tooltip
                      cursor={{ fill: theme.tableRowHover }}
                      contentStyle={{
                        background: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: "12px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                        padding: "12px 16px",
                      }}
                      formatter={(value) => [
                        `R$ ${value.toLocaleString("pt-BR")}`,
                        "Carga Tributária",
                      ]}
                      labelStyle={{
                        color: theme.text,
                        fontWeight: 600,
                        marginBottom: 8,
                      }}
                      itemStyle={{
                        color: theme.text,
                        opacity: 0.8,
                        fontSize: 13,
                      }}
                    />
                    <Bar
                      dataKey="valor"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={55}
                      filter="url(#shadow2)"
                    >
                      {(evolucaoTemporalData.length > 0 ? evolucaoTemporalData : [
                        { ano: "2024", valor: 0, label: "Atual" },
                        { ano: "2027", valor: 0, label: "Início" },
                        { ano: "2033", valor: 0, label: "100%" },
                      ]).map((entry, index) => {
                        const colorMap = {
                          "2024": "url(#barGradient2024)",
                          "2027": "url(#barGradient2027)",
                          "2028": "url(#barGradient2028)",
                          "2029": "url(#barGradient2029)",
                          "2030": "url(#barGradient2030)",
                          "2031": "url(#barGradient2031)",
                          "2032": "url(#barGradient2032)",
                          "2033": "url(#barGradient2033)",
                        };
                        return (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={colorMap[entry.ano] || "url(#barGradient2027)"} 
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-footer">
                <div className="footer-stat">
                  <span className="stat-label">Variação Total</span>
                  <span className={`stat-value ${reformaKpis.variacaoPercentual >= 0 ? 'increase' : 'decrease'}`}>
                    {reformaKpis.variacaoPercentual >= 0 ? '+' : ''}{reformaKpis.variacaoPercentual.toFixed(1)}%
                  </span>
                </div>
                <div className="footer-stat">
                  <span className="stat-label">{reformaKpis.impacto >= 0 ? 'Aumento Absoluto' : 'Economia Absoluta'}</span>
                  <span className="stat-value">{reformaKpis.impacto >= 0 ? '+' : ''}{formatCurrency(reformaKpis.impacto)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabela de Projeção de Débitos - Nova versão moderna */}
          <div className="projecao-debitos-section">
            <div className="section-header-row">
              <div className="section-title">
                <BiSpreadsheet className="section-icon" />
                <h3>Projeção de Débitos: Cenário Atual vs 2027</h3>
              </div>
              <div className="header-actions">
                <div className="table-search-input">
                  <HiOutlineSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Buscar produto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <span className="products-count">
                  {
                    productsData.filter(
                      (p) =>
                        p.nome
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        p.codigo
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    ).length
                  }{" "}
                  produtos
                </span>
              </div>
            </div>

            <div className="projecao-table-container">
              <table className="projecao-table">
                <thead>
                  <tr>
                    <th className="col-codigo">Código</th>
                    <th className="col-descricao">Descrição</th>
                    <th className="col-valor">Total Vendas</th>
                    <th className="col-percent">Participação</th>
                    <th className="col-debitos-header" colSpan="2">
                      <span className="header-group">Débitos Atuais</span>
                    </th>
                    <th className="col-cbs-header" colSpan="3">
                      <span className="header-group cbs">2027 - CBS Plena</span>
                    </th>
                    <th className="col-variacao-header" colSpan="2">
                      <span className="header-group variacao">
                        Variação de Débitos
                      </span>
                    </th>
                  </tr>
                  <tr className="sub-header">
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th className="sub-col">PIS/COFINS</th>
                    <th className="sub-col">Total</th>
                    <th className="sub-col">IVA Dual</th>
                    <th className="sub-col">CBS</th>
                    <th className="sub-col">Alíq. Total</th>
                    <th className="sub-col">Diferença</th>
                    <th className="sub-col">Impacto</th>
                  </tr>
                </thead>
                <tbody>
                  {productsData
                    .filter(
                      (p) =>
                        p.nome
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        p.codigo
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    )
                    .slice(0, 15)
                    .map((product, index) => {
                      const totalVendas = product.valorVendas || 850000;
                      const participacao = (
                        (totalVendas / 8500000) *
                        100
                      ).toFixed(2);
                      const pisCofins = totalVendas * 0.0925;
                      const debitosAtuais = pisCofins;
                      const ivaDual = totalVendas * 0.088;
                      const cbs = totalVendas * 0.088;
                      const aliqTotal = 8.8;
                      const diferenca = ivaDual - debitosAtuais;
                      const impactoPercent = (
                        (diferenca / debitosAtuais) *
                        100
                      ).toFixed(1);
                      const impactoClass =
                        diferenca > 0
                          ? "aumento"
                          : diferenca < 0
                          ? "reducao"
                          : "neutro";
                      const impactoLevel =
                        Math.abs(diferenca) > 5000 ? "alto" : "normal";

                      return (
                        <tr
                          key={product.id}
                          className={`data-row ${impactoClass}`}
                        >
                          <td className="col-codigo">
                            <span className="codigo-badge">
                              {product.codigo}
                            </span>
                          </td>
                          <td className="col-descricao">
                            <span className="produto-nome">{product.nome}</span>
                          </td>
                          <td className="col-valor">
                            <span className="valor-principal">
                              {formatNumber(totalVendas)}
                            </span>
                          </td>
                          <td className="col-percent">
                            <div className="percent-bar-container">
                              <div
                                className="percent-bar"
                                style={{
                                  width: `${Math.min(participacao * 10, 100)}%`,
                                }}
                              ></div>
                              <span className="percent-value">
                                {participacao}%
                              </span>
                            </div>
                          </td>
                          <td className="col-debito">
                            {formatNumber(pisCofins)}
                          </td>
                          <td className="col-debito total">
                            {formatNumber(debitosAtuais)}
                          </td>
                          <td className="col-cbs">{formatNumber(ivaDual)}</td>
                          <td className="col-cbs">{formatNumber(cbs)}</td>
                          <td className="col-aliq">{aliqTotal}%</td>
                          <td className={`col-diferenca ${impactoClass}`}>
                            {diferenca >= 0 ? "+" : ""}
                            {formatNumber(diferenca)}
                          </td>
                          <td className="col-impacto">
                            <span
                              className={`impacto-badge ${impactoClass} ${impactoLevel}`}
                            >
                              {impactoClass === "aumento"
                                ? "Alto Aumento"
                                : impactoClass === "reducao"
                                ? "Redução"
                                : "Neutro"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="table-legend">
              <div className="legend-item reducao">
                <span className="legend-dot"></span>
                <span>Alta Redução de Débitos</span>
              </div>
              <div className="legend-item reducao-leve">
                <span className="legend-dot"></span>
                <span>Redução de Débitos</span>
              </div>
              <div className="legend-item neutro">
                <span className="legend-dot"></span>
                <span>Impacto Neutro</span>
              </div>
              <div className="legend-item aumento-leve">
                <span className="legend-dot"></span>
                <span>Aumento de Débitos</span>
              </div>
              <div className="legend-item aumento">
                <span className="legend-dot"></span>
                <span>Alto Aumento de Débitos</span>
              </div>
            </div>
          </div>

          {/* Tabela de Projeção de Créditos */}
          <div className="projecao-debitos-section creditos">
            <div className="section-header-row">
              <div className="section-title">
                <BiSpreadsheet className="section-icon" />
                <h3>Projeção de Créditos: Cenário Atual vs 2027</h3>
              </div>
              <div className="header-actions">
                <div className="table-search-input">
                  <HiOutlineSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Buscar produto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <span className="products-count">
                  {
                    productsData.filter(
                      (p) =>
                        p.nome
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        p.codigo
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    ).length
                  }{" "}
                  produtos
                </span>
              </div>
            </div>

            <div className="projecao-table-container">
              <table className="projecao-table creditos">
                <thead>
                  <tr>
                    <th className="col-codigo">Código</th>
                    <th className="col-descricao">Descrição</th>
                    <th className="col-valor">Total Aquisições</th>
                    <th className="col-percent">Participação</th>
                    <th className="col-qtd">Qtd Itens</th>
                    <th className="col-creditos-header" colSpan="2">
                      <span className="header-group creditos-atuais">
                        Créditos Atuais
                      </span>
                    </th>
                    <th className="col-cbs-header" colSpan="3">
                      <span className="header-group cbs">2027 - CBS Plena</span>
                    </th>
                    <th className="col-variacao-header" colSpan="2">
                      <span className="header-group variacao-creditos">
                        Variação de Créditos
                      </span>
                    </th>
                  </tr>
                  <tr className="sub-header">
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th className="sub-col">PIS/COFINS</th>
                    <th className="sub-col">Total Atual</th>
                    <th className="sub-col">IVA Dual</th>
                    <th className="sub-col">CBS</th>
                    <th className="sub-col">Alíq. Total</th>
                    <th className="sub-col">Ganho/Perda</th>
                    <th className="sub-col">Impacto</th>
                  </tr>
                </thead>
                <tbody>
                  {productsData
                    .filter(
                      (p) =>
                        p.nome
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        p.codigo
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    )
                    .slice(0, 15)
                    .map((product, index) => {
                      const totalAquisicoes =
                        (product.valorVendas || 850000) * 0.6;
                      const participacao = (
                        (totalAquisicoes / 5100000) *
                        100
                      ).toFixed(2);
                      const qtdItens = Math.floor(Math.random() * 3) + 1;
                      const pisCofinsCredito = totalAquisicoes * 0.0925;
                      const creditosAtuais = pisCofinsCredito;
                      const ivaDualCredito = totalAquisicoes * 0.088;
                      const cbsCredito = totalAquisicoes * 0.088;
                      const aliqTotal = 8.8;
                      const ganhoPerdaCredito = ivaDualCredito - creditosAtuais;
                      const impactoClassCredito =
                        ganhoPerdaCredito < 0
                          ? "perda"
                          : ganhoPerdaCredito > 0
                          ? "ganho"
                          : "neutro";
                      const impactoLevelCredito =
                        Math.abs(ganhoPerdaCredito) > 3000 ? "alto" : "normal";

                      return (
                        <tr
                          key={`credito-${product.id}`}
                          className={`data-row ${impactoClassCredito}`}
                        >
                          <td className="col-codigo">
                            <span className="codigo-badge credito">
                              {product.codigo}
                            </span>
                          </td>
                          <td className="col-descricao">
                            <span className="produto-nome">{product.nome}</span>
                          </td>
                          <td className="col-valor">
                            <span className="valor-principal">
                              {formatNumber(totalAquisicoes)}
                            </span>
                          </td>
                          <td className="col-percent">
                            <div className="percent-bar-container">
                              <div
                                className="percent-bar credito"
                                style={{
                                  width: `${Math.min(participacao * 10, 100)}%`,
                                }}
                              ></div>
                              <span className="percent-value">
                                {participacao}%
                              </span>
                            </div>
                          </td>
                          <td className="col-qtd">{qtdItens}</td>
                          <td className="col-credito-atual">
                            {formatNumber(pisCofinsCredito)}
                          </td>
                          <td className="col-credito-atual total">
                            {formatNumber(creditosAtuais)}
                          </td>
                          <td className="col-cbs">
                            {formatNumber(ivaDualCredito)}
                          </td>
                          <td className="col-cbs">
                            {formatNumber(cbsCredito)}
                          </td>
                          <td className="col-aliq">{aliqTotal}%</td>
                          <td
                            className={`col-ganho-perda ${impactoClassCredito}`}
                          >
                            {ganhoPerdaCredito >= 0 ? "+" : ""}
                            {formatNumber(ganhoPerdaCredito)}
                          </td>
                          <td className="col-impacto">
                            <span
                              className={`impacto-badge ${impactoClassCredito} ${impactoLevelCredito}`}
                            >
                              {impactoClassCredito === "perda"
                                ? "Alta Perda"
                                : impactoClassCredito === "ganho"
                                ? "Ganho"
                                : "Neutro"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="table-legend creditos">
              <div className="legend-item ganho-alto">
                <span className="legend-dot"></span>
                <span>Alto Ganho de Créditos</span>
              </div>
              <div className="legend-item ganho">
                <span className="legend-dot"></span>
                <span>Ganho de Créditos</span>
              </div>
              <div className="legend-item neutro">
                <span className="legend-dot"></span>
                <span>Impacto Neutro</span>
              </div>
              <div className="legend-item perda">
                <span className="legend-dot"></span>
                <span>Perda de Créditos</span>
              </div>
              <div className="legend-item perda-alta">
                <span className="legend-dot"></span>
                <span>Alta Perda de Créditos</span>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      )}

      {/* Tooltip Flutuante - Fora do card */}
      {chartTooltip.visible && chartTooltip.data && (
        <div
          className="chart-tooltip-floating"
          style={{
            position: "fixed",
            left: chartTooltip.x - 280,
            top: chartTooltip.y + 20,
            pointerEvents: "none",
            zIndex: 9999,
            background: "rgba(17, 24, 39, 0.95)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "12px",
            padding: "18px 24px",
            minWidth: "260px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(10px)",
            animation: "tooltipFadeIn 0.15s ease-out",
            transition: "left 0.1s ease-out, top 0.1s ease-out",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <span
              style={{ fontSize: "0.85em", fontWeight: 600, color: "#fff" }}
            >
              {chartTooltip.data.month}
            </span>
            <span
              style={{
                fontSize: "0.75em",
                color: "#9ca3af",
                background: "rgba(255, 255, 255, 0.05)",
                padding: "3px 8px",
                borderRadius: "4px",
              }}
            >
              {chartTooltip.type === "compras" && "Compras"}
              {chartTooltip.type === "vendas" && "Vendas"}
              {chartTooltip.type === "impostos" && "Impostos"}
            </span>
          </div>

          {chartTooltip.type === "compras" && (
            <div>
              <div
                style={{
                  fontSize: "1.4em",
                  fontWeight: 700,
                  color: "#3b82f6",
                  marginBottom: "4px",
                }}
              >
                {formatNumber(chartTooltip.data.value)}
              </div>
              <span style={{ fontSize: "0.75em", color: "#9ca3af" }}>
                Total de compras no mês
              </span>
            </div>
          )}

          {chartTooltip.type === "vendas" && (
            <div>
              <div
                style={{
                  fontSize: "1.4em",
                  fontWeight: 700,
                  color: "#10b981",
                  marginBottom: "4px",
                }}
              >
                {formatNumber(chartTooltip.data.value)}
              </div>
              <span style={{ fontSize: "0.75em", color: "#9ca3af" }}>
                Total de vendas no mês
              </span>
            </div>
          )}

          {chartTooltip.type === "impostos" && (
            <div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "40px",
                  }}
                >
                  <span style={{ fontSize: "0.85em", color: "#9ca3af" }}>
                    ICMS
                  </span>
                  <span
                    style={{
                      fontSize: "0.9em",
                      fontWeight: 600,
                      color: "#fff",
                    }}
                  >
                    {formatNumber(chartTooltip.data.detalhes.icms)}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "40px",
                  }}
                >
                  <span style={{ fontSize: "0.85em", color: "#9ca3af" }}>
                    PIS/COFINS
                  </span>
                  <span
                    style={{
                      fontSize: "0.9em",
                      fontWeight: 600,
                      color: "#fff",
                    }}
                  >
                    {formatNumber(chartTooltip.data.detalhes.pisCofins)}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "40px",
                  }}
                >
                  <span style={{ fontSize: "0.85em", color: "#9ca3af" }}>
                    IBS
                  </span>
                  <span
                    style={{
                      fontSize: "0.9em",
                      fontWeight: 600,
                      color: "#fff",
                    }}
                  >
                    {formatNumber(chartTooltip.data.detalhes.ibs)}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "40px",
                  }}
                >
                  <span style={{ fontSize: "0.85em", color: "#9ca3af" }}>
                    CBS
                  </span>
                  <span
                    style={{
                      fontSize: "0.9em",
                      fontWeight: 600,
                      color: "#fff",
                    }}
                  >
                    {formatNumber(chartTooltip.data.detalhes.cbs)}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "40px",
                  }}
                >
                  <span style={{ fontSize: "0.85em", color: "#9ca3af" }}>
                    IS
                  </span>
                  <span
                    style={{
                      fontSize: "0.9em",
                      fontWeight: 600,
                      color: "#fff",
                    }}
                  >
                    {formatNumber(chartTooltip.data.detalhes.is)}
                  </span>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "12px",
                  paddingTop: "10px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <span style={{ fontSize: "0.85em", color: "#9ca3af" }}>
                  Total
                </span>
                <span
                  style={{
                    fontSize: "1.1em",
                    fontWeight: 700,
                    color: "#ef4444",
                  }}
                >
                  {formatNumber(chartTooltip.data.total)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </Container>
  );
}
